import { alpha } from '@mui/material/styles';
import COLORS from '../../constants/colors';

export const GRID_ROW_EVEN_CLASS = 'grid-row-even';
export const GRID_ROW_ODD_CLASS = 'grid-row-odd';

/**
 * Shared grid styling. Wide tables left large gaps between sparse columns, so a
 * value and its header drifted apart and rows read as one block: column rules
 * and alternating row bands give the eye something to track.
 * Rules are ordered zebra -> hover -> selected, since they match on specificity.
 */
export const baseGridSx = {
  borderRadius: '12px',
  borderColor: alpha(COLORS.MAIN_DARK, 0.08),

  // --- header -------------------------------------------------------------
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: alpha(COLORS.MAIN_DARK, 0.04),
    borderBottom: `1px solid ${alpha(COLORS.MAIN_DARK, 0.14)}`,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: COLORS.DARK_GREY,
  },
  '& .MuiDataGrid-columnHeader:not(:last-of-type)': {
    borderRight: `1px solid ${alpha(COLORS.MAIN_DARK, 0.08)}`,
  },
  // The built-in separator handle duplicates the column rule above.
  '& .MuiDataGrid-columnSeparator': {
    color: 'transparent',
  },
  '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
    color: alpha(COLORS.MAIN_DARK, 0.24),
  },
  '& .MuiDataGrid-aggregationColumnHeaderLabel': {
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: COLORS.LIGHT_GRAY,
  },

  // --- cells --------------------------------------------------------------
  '& .MuiDataGrid-cell': {
    fontSize: '0.8125rem',
    color: COLORS.MAIN_DARK,
    borderBottom: `1px solid ${alpha(COLORS.MAIN_DARK, 0.07)}`,
  },
  '& .MuiDataGrid-cell:not(:last-of-type)': {
    borderRight: `1px solid ${alpha(COLORS.MAIN_DARK, 0.05)}`,
  },
  // Grouping rows head a block of children, so they carry the weight.
  '& .MuiDataGrid-groupingCriteriaCell, & .MuiDataGrid-cell--withRenderer .MuiDataGrid-groupingCriteriaCell': {
    fontWeight: 600,
  },

  // --- row states ---------------------------------------------------------
  [`& .MuiDataGrid-row.${GRID_ROW_ODD_CLASS}`]: {
    backgroundColor: alpha(COLORS.MAIN_DARK, 0.02),
  },
  [`& .MuiDataGrid-row.${GRID_ROW_EVEN_CLASS}:hover, & .MuiDataGrid-row.${GRID_ROW_ODD_CLASS}:hover`]: {
    backgroundColor: alpha(COLORS.PRIMARY_BLUE, 0.06),
  },
  [`& .MuiDataGrid-row.${GRID_ROW_EVEN_CLASS}.Mui-selected, & .MuiDataGrid-row.${GRID_ROW_ODD_CLASS}.Mui-selected`]: {
    backgroundColor: alpha(COLORS.PRIMARY_BLUE, 0.1),
  },
  [`& .MuiDataGrid-row.${GRID_ROW_EVEN_CLASS}.Mui-selected:hover,
    & .MuiDataGrid-row.${GRID_ROW_ODD_CLASS}.Mui-selected:hover`]: {
    backgroundColor: alpha(COLORS.PRIMARY_BLUE, 0.14),
  },

  // --- focus --------------------------------------------------------------
  // Drop the outline that fires on mouse click, keep it for keyboard focus.
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within':
    {
      outline: 'none',
    },
  '& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible': {
    outline: `2px solid ${COLORS.PRIMARY_BLUE}`,
    outlineOffset: '-2px',
  },

  // --- chrome -------------------------------------------------------------
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${alpha(COLORS.MAIN_DARK, 0.14)}`,
  },
  '& .MuiDataGrid-toolbarContainer': {
    padding: '10px 12px',
    gap: '8px',
    borderBottom: `1px solid ${alpha(COLORS.MAIN_DARK, 0.08)}`,
  },
};

/** Alternating row bands. Keyed to the page index so stripes survive virtual scrolling. */
export function baseGetRowClassName({ indexRelativeToCurrentPage }: { indexRelativeToCurrentPage: number }) {
  return indexRelativeToCurrentPage % 2 === 0 ? GRID_ROW_EVEN_CLASS : GRID_ROW_ODD_CLASS;
}
